import React, { useState, useEffect } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import { horseHead} from '@lucide/lab';
import { Upload, Camera, X } from 'lucide-react';

import { useNotification } from "../../contexts/NotificationContext";
import { useSubscription } from "../../hooks/useSubscription";
import { getApiUrl } from "../../config/api";

export default function HorseInfo() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showSuccess, showError, showWarning } = useNotification();
  const { hasFeature } = useSubscription();
  const [familyMembers, setFamilyMembers] = useState([]);
  const [horse, setHorse] = useState({
    name: "",
    breed: "",
    dob: "",
    sex: "",
    color: "",
    notes: "",
    age: "0",
    weight: "0",
    registration_number: "",
    microchip_number: "",
    horse_image: "",
    family_member_id: ""
  });
  const [showCameraOptions, setShowCameraOptions] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    fetchHorse();
    if (hasFeature('family_plan')) {
      fetchFamilyMembers();
    }
    // Check if user is on mobile device
    setIsMobile(/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent));
    
    // Add click outside handler for camera options
    const handleClickOutside = (event) => {
      if (showCameraOptions && !event.target.closest('.camera-options-container')) {
        setShowCameraOptions(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showCameraOptions, hasFeature]);

  const fetchHorse = async () => {
    try {
      const res = await axios.get(getApiUrl(`/api/horses/${id}`), {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      const horseData = res.data;
      // Calculate age from date of birth if available
      if (horseData.dob) {
        horseData.age = calculateAge(horseData.dob);
      }
      // Set default values for age and weight if they are null/undefined
      if (!horseData.age && horseData.age !== 0) {
        horseData.age = horseData.dob ? calculateAge(horseData.dob) : "0";
      }
      if (!horseData.weight && horseData.weight !== 0) {
        horseData.weight = "0";
      }
      // Handle family_member_id - if null, set to "self" for display
      if (horseData.family_member_id === null) {
        horseData.family_member_id = "self";
      }
      setHorse(horseData);
    } catch (err) {
      console.error("Failed to fetch horse", err);
      showError("Failed to fetch horse information");
    }
  };

  const fetchFamilyMembers = async () => {
    try {
      const res = await axios.get(getApiUrl("/api/settings/family-members"), {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      setFamilyMembers(res.data);
    } catch (error) {
      console.error("Error fetching family members:", error);
    }
  };

  const updateHorse = async () => {
    // Validate required fields
    if (!horse.name.trim()) {
      showWarning("Horse name is required");
      return;
    }

    // Prepare horse data with proper family_member_id handling
    const horseData = {
      ...horse,
      // Handle "self" selection - set to null for primary account
      family_member_id: horse.family_member_id === "self" ? null : (horse.family_member_id || null)
    };

    try {
      await axios.put(getApiUrl(`/api/horses/${id}`), horseData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      showSuccess("Horse updated successfully!");
      navigate("/horses");
    } catch (err) {
      console.error("Failed to update horse", err);
      showError("Failed to update horse. Please try again.");
    }
  };

  const handleChange = (e) => {
    setHorse({ ...horse, [e.target.name]: e.target.value });
  };

  const goBack = () => navigate("/horses");

  // Calculate age from date of birth
  const calculateAge = (dob) => {
    if (!dob) return "";
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    
    return age >= 0 ? age : "";
  };

  // Update age when date of birth changes
  const handleDateChange = (e) => {
    const newDob = e.target.value;
    setHorse({ ...horse, dob: newDob, age: calculateAge(newDob) });
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      processImageFile(file);
    }
  };

  const processImageFile = (file) => {
    // Check file type
    if (!file.type.startsWith('image/')) {
      showWarning("Please select an image file");
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new Image();
      img.onload = () => {
        // Dynamic sizing based on original dimensions
        let targetSize = 200; // Default size
        let quality = 0.8; // Default quality
        
        // If image is very large, increase target size but maintain aspect ratio
        if (img.width > 1000 || img.height > 1000) {
          targetSize = 400; // Larger size for high-res images
        } else if (img.width > 500 || img.height > 500) {
          targetSize = 300; // Medium size for medium-res images
        }
        
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        // Set canvas size to target size (square)
        canvas.width = targetSize;
        canvas.height = targetSize;
        
        // Calculate dimensions to maintain aspect ratio
        const size = Math.min(img.width, img.height);
        const startX = (img.width - size) / 2;
        const startY = (img.height - size) / 2;
        
        // Draw cropped and resized image
        ctx.drawImage(img, startX, startY, size, size, 0, 0, targetSize, targetSize);
        
        // Dynamic compression based on target size
        if (targetSize === 400) {
          quality = 0.7; // Lower quality for larger images
        } else if (targetSize === 300) {
          quality = 0.75; // Medium quality for medium images
        } else {
          quality = 0.8; // Higher quality for smaller images
        }
        
        // Convert to base64 with dynamic compression
        let resizedImage = canvas.toDataURL('image/jpeg', quality);
        
        // Check if the result is still too large and compress further if needed
        if (resizedImage.length > 2 * 1024 * 1024) {
          // If still too large, reduce quality further
          quality = Math.max(0.3, quality - 0.2);
          resizedImage = canvas.toDataURL('image/jpeg', quality);
          
          // If still too large, reduce size
          if (resizedImage.length > 2 * 1024 * 1024) {
            targetSize = Math.floor(targetSize * 0.8);
            canvas.width = targetSize;
            canvas.height = targetSize;
            ctx.drawImage(img, startX, startY, size, size, 0, 0, targetSize, targetSize);
            resizedImage = canvas.toDataURL('image/jpeg', 0.6);
          }
        }
        
        setHorse({ ...horse, horse_image: resizedImage });
      };
      img.src = event.target.result;
    };
    reader.readAsDataURL(file);
  };

  const handleCameraCapture = () => {
    setShowCameraOptions(false);
    
    // Create a file input with camera capture attribute
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.capture = 'environment'; // Use back camera by default
    
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (file) {
        processImageFile(file);
      }
    };
    
    input.click();
  };

  const handleFrontCameraCapture = () => {
    setShowCameraOptions(false);
    
    // Create a file input with front camera capture
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.capture = 'user'; // Use front camera
    
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (file) {
        processImageFile(file);
      }
    };
    
    input.click();
  };

  const removeImage = () => {
    setHorse({ ...horse, horse_image: "" });
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">

      
      <button
        onClick={goBack}
        className="mb-4 px-4 py-2 bg-gray-100 border rounded hover:bg-gray-200 text-sm"
      >
        ← Back to Horses
      </button>

      <div className="bg-white shadow-md rounded-md p-6 border border-gray-200">
        <h2 className="text-2xl font-bold mb-6 text-gray-800 flex items-center">
          <horseHead className="mr-2 h-8 w-8 text-blue-600" />
          Edit Horse Information
        </h2>
        
        {/* Horse Image Section */}
        <div className="mb-6 p-4 border border-gray-200 rounded-lg bg-gray-50">
          <label className="block text-sm font-medium text-gray-700 mb-3">Horse Photo</label>
          <div className="flex items-center gap-4">
            {horse.horse_image ? (
              <div className="relative">
                <img 
                  src={horse.horse_image} 
                  alt="Horse" 
                  className="w-24 h-24 object-cover rounded-lg border-2 border-gray-300"
                />
                <button
                  onClick={removeImage}
                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600"
                  title="Remove image"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ) : (
              <div className="w-24 h-24 bg-gray-200 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center">
                <span className="text-gray-400 text-xs text-center">No image</span>
              </div>
            )}
            <div className="flex flex-col gap-2">
                                {/* Mobile Camera Options */}
                  {isMobile && (
                    <div className="flex gap-2 camera-options-container">
                      <button
                        onClick={() => setShowCameraOptions(!showCameraOptions)}
                        className="px-3 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition-colors text-sm flex items-center gap-1"
                      >
                        <Camera className="w-4 h-4" />
                        Camera
                      </button>
                      {showCameraOptions && (
                        <div className="absolute z-10 mt-12 bg-white border border-gray-200 rounded-lg shadow-lg p-2">
                          <button
                            onClick={handleCameraCapture}
                            className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 rounded flex items-center gap-2"
                          >
                            📷 Back Camera
                          </button>
                          <button
                            onClick={handleFrontCameraCapture}
                            className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 rounded flex items-center gap-2"
                          >
                            🤳 Front Camera
                          </button>
                        </div>
                      )}
                    </div>
                  )}
              
              {/* File Upload */}
              <input
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden"
                id="horse-image-upload"
              />
              <label
                htmlFor="horse-image-upload"
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors cursor-pointer text-sm text-center flex items-center justify-center gap-2"
              >
                <Upload className="w-4 h-4" />
                {horse.horse_image ? 'Change Photo' : 'Upload Photo'}
              </label>
                             <p className="text-xs text-gray-500">Automatically optimized to fit database requirements</p>
              {isMobile && (
                <p className="text-xs text-green-600">📱 Use Camera button for mobile photos</p>
              )}
            </div>
          </div>
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Horse Name *</label>
            <input
              name="name"
              type="text"
              placeholder="e.g., Thunder, Bella, Shadow"
              value={horse.name}
              onChange={handleChange}
              className="border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 w-full"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Breed</label>
            <input
              name="breed"
              type="text"
              placeholder="e.g., Quarter Horse, Thoroughbred, Arabian"
              value={horse.breed}
              onChange={handleChange}
              className="border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 w-full"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date of Birth</label>
            <input
              name="dob"
              type="date"
              value={horse.dob ? horse.dob.split("T")[0] : ""}
              onChange={handleDateChange}
              className="border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 w-full"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Sex</label>
            <select
              name="sex"
              value={horse.sex}
              onChange={handleChange}
              className="border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 w-full"
            >
              <option value="">Select Sex</option>
              <option value="Stallion">Stallion</option>
              <option value="Mare">Mare</option>
              <option value="Gelding">Gelding</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Color</label>
            <input
              name="color"
              type="text"
              placeholder="e.g., Bay, Chestnut, Black"
              value={horse.color}
              onChange={handleChange}
              className="border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 w-full"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Age (Years)</label>
            <input
              name="age"
              type="number"
              placeholder="Enter age or leave empty for auto-calculation"
              value={horse.age}
              onChange={handleChange}
              className="border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 w-full"
              min="0"
              max="40"
            />
            <p className="text-xs text-gray-500 mt-1">Enter manually or leave empty to auto-calculate from date of birth</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Weight (lbs)</label>
            <input
              name="weight"
              type="number"
              placeholder="Weight in pounds (0 if unknown)"
              value={horse.weight}
              onChange={handleChange}
              className="border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 w-full"
              min="0"
              max="3000"
            />
            <p className="text-xs text-gray-500 mt-1">Defaults to 0 if no value provided</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Registration Number</label>
            <input
              name="registration_number"
              type="text"
              placeholder="Registration number"
              value={horse.registration_number}
              onChange={handleChange}
              className="border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 w-full"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Microchip Number</label>
            <input
              name="microchip_number"
              type="text"
              placeholder="Microchip number"
              value={horse.microchip_number}
              onChange={handleChange}
              className="border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 w-full"
            />
          </div>
          {hasFeature('family_plan') && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Family Member</label>
              <select
                name="family_member_id"
                value={horse.family_member_id}
                onChange={handleChange}
                className="border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 w-full"
              >
                <option value="">Select family member (optional)</option>
                <option value="self">Myself (Primary Account)</option>
                {familyMembers.map((member) => (
                  <option key={member.id} value={member.id}>
                    {member.name} {member.relationship ? `(${member.relationship})` : ''}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
        
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
          <textarea
            name="notes"
            placeholder="Additional notes about the horse..."
            value={horse.notes}
            onChange={handleChange}
            className="border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 w-full"
            rows="3"
          />
        </div>
        
        <div className="flex gap-3">
          <button 
            onClick={updateHorse} 
            className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700 transition-colors"
          >
            Save Changes
          </button>
          <button 
            onClick={goBack}
            className="px-6 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
